import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from 'lib/utils';

interface SelectOption {
    value: string | number;
    label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
    label?: string;
    options: SelectOption[];
    error?: string;
    required?: boolean;
    fullWidth?: boolean;
    placeholder?: string;
}

/**
 * 호환성을 위한 native select 래퍼. 더 풍부한 검색/그룹/커스텀 옵션이 필요하면
 * ui/shadcn/select 또는 ui/SearchSelect를 사용.
 */
const Select: React.FC<SelectProps> = ({
    label,
    options,
    error,
    required,
    fullWidth = true,
    placeholder = '선택하세요',
    className = '',
    id,
    ...props
}) => {
    const selectId = id || (label ? `select-${label.replace(/\s+/g, '-')}` : undefined);

    return (
        <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
            {label && (
                <label htmlFor={selectId} className="text-sm font-medium text-neutral-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </label>
            )}
            <div className="relative">
                <select
                    id={selectId}
                    aria-invalid={Boolean(error)}
                    className={cn(
                        'flex h-9 w-full appearance-none rounded border border-input bg-background px-3 pr-9 py-1 text-base text-foreground transition-colors duration-fast',
                        'focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring',
                        'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
                        'aria-[invalid=true]:border-destructive',
                        className
                    )}
                    {...props}
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            </div>
            {error && <span className="text-xs text-destructive">{error}</span>}
        </div>
    );
};

export default Select;
